import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import SV from 'server-vars';
import { sessionStorage, localStorage } from 'dibs-browser-storage';
import { ReactRelayContext } from 'react-relay';
import { IntlProvider } from 'dibs-react-intl';
import getRelayEnvironment from '../relayClientEnvironment';
import { getBuyerId } from 'dibs-cookie-jar';
import { getAbTestV2 } from '../utils/abTest/clientAbTestV2';
import { AUTHENTICATED_THIS_SESSION } from '../authModal/authentication/userStorageConstants';

const locale = SV.get('locale');
const messages = SV.get('messages');

import {
    EmailOptinToastShownKey,
    EmailOptinToastTimesShownKey,
    EmailOptinToastTestName,
} from './constants';

const LazyEmailsOptinToast = lazy(() => import('./EmailsOptInToast'));
/**
 * Toast display rules:
 *
 * ONLY show to European users (IP+DE+FR+EN sites) who are LOGGED IN who have NOT YET OPTED IN TO MARKETING EMAILS** (marketing to confirm exact logic)
 * Fire the toast for only the first item the user favorites in a session (one fire per session ONLY)
 * Applies to every session for respective user group, but only on the first item a user favorites
 * Show on any page except homepage (exclude from homepage because there’s an auctions toast on homepage)
 * If the user navigates to a different page, the toast should go away regardless of what state it’s in
 * If user dismisses the message 5 times (lifetime) without opting in, do not show this toast ever again
 * If a user logs in OR registers within a session, DO NOT show this toast until the next session if applicable
 * Once the user has opted in by clicking the CTA, dismiss the confirmation toast after 8 seconds
 * If the user taps/clicks outside the modal or toast area OR the X on the confirmation, dismiss the confirmation
 */
export function renderEmailsOptInToast(): void {
    if (typeof window === undefined) {
        return;
    }

    if (!getAbTestV2(EmailOptinToastTestName)?.config?.isActive) {
        return;
    }

    const isMobile = SV.get('settings.isMobile');
    const pageType = SV.get('settings.pageType');
    const isHomePage = pageType === 'home';

    if (isMobile) {
        return;
    }

    // do not show on homepage
    if (isHomePage) {
        return;
    }

    // show only for logged in users
    if (!getBuyerId(document.cookie)) {
        return;
    }

    // do not show on the same session when auth flow was triggered
    if (sessionStorage.getItem(AUTHENTICATED_THIS_SESSION)) {
        return;
    }

    // show once per session
    const optinToastShown = sessionStorage.getItem(EmailOptinToastShownKey);
    if (optinToastShown) {
        return;
    }

    // do not show more than 5 times
    const optinToastTimesShown = localStorage.getItem(EmailOptinToastTimesShownKey);
    if (Number(optinToastTimesShown) >= 5) {
        return;
    }

    const div = document.createElement('div');
    document.body.appendChild(div);
    const root = createRoot(div);

    root.render(
        <IntlProvider locale={locale} messages={messages}>
            <ReactRelayContext.Provider value={{ environment: getRelayEnvironment() }}>
                <Suspense fallback={''}>
                    <LazyEmailsOptinToast />
                </Suspense>
            </ReactRelayContext.Provider>
        </IntlProvider>
    );
}
